import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useIncidentTypes, useCourierIncidentKinds } from '../../hooks';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@shared/components/ui/Spinner';

// Hook para obtener couriers
const useCouriers = () => {
  const httpClient = createHttpClient(apiClientWH);

  return useQuery({
    queryKey: ['catalog-shortcuts-couriers'],
    queryFn: async () => {
      const raw = await httpClient.get<any>('/couriers');
      const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
      return list;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export interface CatalogShortcutsRef {
  openInternalCatalog: () => void;
  openCourierCatalog: () => void;
}

interface CatalogShortcutsProps {
  hideButtons?: boolean;
}

export const CatalogShortcuts = forwardRef<CatalogShortcutsRef, CatalogShortcutsProps>(
  ({ hideButtons = false }, ref) => {
    const { t } = useTranslation('mappingsModule');
    const [selectedCourierId, setSelectedCourierId] = useState<number | null>(null);
    const internalCatalogModalRef = useRef<ModalCenterRef>(null);
    const courierCatalogModalRef = useRef<ModalCenterRef>(null);
    const courierSelectModalRef = useRef<ModalCenterRef>(null);

    const { data: incidentTypes = [], isLoading: isLoadingTypes } = useIncidentTypes();
    const { data: couriers = [], isLoading: isLoadingCouriers } = useCouriers();
    const { data: courierKinds = [], isLoading: isLoadingCourierKinds } = useCourierIncidentKinds(selectedCourierId);

    // Debug: Verificar datos recibidos
    console.log('🔍 CatalogShortcuts - courierKinds:', courierKinds);
    console.log('🔍 CatalogShortcuts - incidentTypes:', incidentTypes);

    const handleOpenInternalCatalog = () => {
      internalCatalogModalRef.current?.openModal();
    };

    const handleOpenCourierCatalog = () => {
      courierSelectModalRef.current?.openModal();
    };

    const handleCourierSelect = (courierId: number) => {
      setSelectedCourierId(courierId);
      courierSelectModalRef.current?.closeModal();
      courierCatalogModalRef.current?.openModal();
    };

    const handleCloseCourierCatalog = () => {
      // Solo resetear el estado, el modal se cierra automáticamente
      setSelectedCourierId(null);
    };

    useImperativeHandle(ref, () => ({
      openInternalCatalog: handleOpenInternalCatalog,
      openCourierCatalog: handleOpenCourierCatalog,
    }));

    return (
      <>
        {!hideButtons && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={handleOpenInternalCatalog}
              data-testid="view-internal-catalog-button"
            >
              {t('shortcuts.view_internal_catalog')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleOpenCourierCatalog}
              data-testid="view-courier-catalog-button"
            >
              {t('shortcuts.view_courier_catalog')}
            </Button>
          </div>
        )}

        {/* Modal: Catálogo Interno */}
        <ModalCenter ref={internalCatalogModalRef} title={t('shortcuts.internal_catalog_title')} type="default">
          <div className="max-h-96 overflow-y-auto">
            {isLoadingTypes ? (
              <div className="flex justify-center items-center py-8">
                <Spinner />
              </div>
            ) : incidentTypes.length > 0 ? (
              <div className="space-y-2">
                {incidentTypes.map((type) => (
                  <div key={type.id} className="w-full p-3 border border-card-border rounded-md bg-card-bg text-left">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-title">{type.label || type.name}</span>
                      {type.code && (
                        <Badge
                          variant="outline"
                          className="w-fit text-[10px] leading-none !py-1 bg-card-border text-text"
                        >
                          {type.code}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-complementary-text">{t('form.no_results')}</div>
            )}
          </div>
        </ModalCenter>

        {/* Modal: Seleccionar Courier para ver su catálogo */}
        <ModalCenter ref={courierSelectModalRef} title={t('shortcuts.select_courier_title')} type="default">
          <div className="max-h-96 overflow-y-auto">
            {isLoadingCouriers ? (
              <div className="flex justify-center items-center py-8">
                <Spinner />
              </div>
            ) : couriers.length > 0 ? (
              <div className="space-y-2">
                {couriers.map((courier: any) => (
                  <button
                    key={courier.id}
                    onClick={() => handleCourierSelect(courier.id)}
                    className="w-full p-3 border border-card-border rounded-md bg-card-bg hover:bg-card-border text-left transition-colors"
                  >
                    <span className="text-sm font-medium text-title">{courier.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-complementary-text">{t('form.no_results')}</div>
            )}
          </div>
        </ModalCenter>

        {/* Modal: Catálogo del Courier seleccionado */}
        <ModalCenter
          ref={courierCatalogModalRef}
          title={t('shortcuts.courier_catalog_title', {
            courierName: couriers.find((c: any) => c.id === selectedCourierId)?.name || '',
          })}
          type="default"
          onCloseModal={handleCloseCourierCatalog}
        >
          <div className="max-h-96 overflow-y-auto">
            {isLoadingCourierKinds ? (
              <div className="flex justify-center items-center py-8">
                <Spinner />
              </div>
            ) : courierKinds.length > 0 ? (
              <div className="space-y-2">
                {courierKinds.map((kind) => (
                  <div key={kind.id} className="w-full p-3 border border-card-border rounded-md bg-card-bg text-left">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-title">
                        {kind.external_label || kind.external_code || '-'}
                      </span>
                      {kind.external_code && (
                        <Badge
                          variant="outline"
                          className="w-fit text-[10px] leading-none !py-1 bg-card-border text-text"
                        >
                          {kind.external_code}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-complementary-text">{t('form.no_results')}</div>
            )}
          </div>
        </ModalCenter>
      </>
    );
  },
);

CatalogShortcuts.displayName = 'CatalogShortcuts';
